/**
 * Gallery Service
 * Handles all Firebase operations for the public gallery feature.
 */

import { getDatabase, ref, get, set, remove, update } from 'firebase/database';
import { GALLERY_CONFIG } from '../config/galleryConfig';

/**
 * Check if a user is a trusted publisher.
 * Only reads their own entry — cannot enumerate other users.
 *
 * @param {string} uid
 * @returns {Promise<boolean>}
 */
export async function isTrustedPublisher(uid) {
  if (!uid) return false;
  const db = getDatabase();
  const snap = await get(ref(db, `trustedPublishers/${uid}`));
  return snap.exists();
}

/**
 * Check if a trusted user has already created their public profile.
 * @param {string} uid
 * @returns {Promise<boolean>}
 */
export async function hasCreatedProfile(uid) {
  if (!uid) return false;
  const db = getDatabase();
  const snap = await get(ref(db, `users/${uid}/profileCreated`));
  return snap.exists() && snap.val() === true;
}

/**
 * Count how many of a user's palettes are currently public.
 *
 * @param {Array} userPalettes - Array of palette objects (from getUserPalettes)
 * @returns {number}
 */
export function getPublicPaletteCount(userPalettes) {
  if (!Array.isArray(userPalettes)) return 0;
  return userPalettes.filter(p => p.isPublic === true).length;
}

/**
 * Publish a palette (set isPublic: true).
 * Runs pre-checks before writing.
 *
 * @param {string} userId
 * @param {string} paletteId
 * @param {Array} userPalettes - Current list of user's palettes (for count check)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function publishPalette(userId, paletteId, userPalettes) {
  if (!userId || !paletteId) {
    return { success: false, error: 'Invalid parameters' };
  }

  const trusted = await isTrustedPublisher(userId);
  if (!trusted) {
    return { success: false, error: 'Not a trusted publisher' };
  }

  const publicCount = getPublicPaletteCount(userPalettes);
  if (publicCount >= GALLERY_CONFIG.MAX_PUBLIC_PALETTES_PER_USER) {
    return {
      success: false,
      error: `Maximum ${GALLERY_CONFIG.MAX_PUBLIC_PALETTES_PER_USER} public palettes reached`
    };
  }

  try {
    const db = getDatabase();
    await update(ref(db, `palettes/${userId}/${paletteId}`), { isPublic: true });
    return { success: true };
  } catch (error) {
    console.error('Failed to publish palette:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Unpublish a palette (set isPublic: false).
 *
 * @param {string} userId
 * @param {string} paletteId
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function unpublishPalette(userId, paletteId) {
  if (!userId || !paletteId) {
    return { success: false, error: 'Invalid parameters' };
  }

  try {
    const db = getDatabase();
    await update(ref(db, `palettes/${userId}/${paletteId}`), { isPublic: false });
    return { success: true };
  } catch (error) {
    console.error('Failed to unpublish palette:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetch the top 50 most popular public palettes.
 * Pre-sorted by the ranking script — no client-side sorting needed.
 *
 * @returns {Promise<{success: boolean, palettes?: Array, error?: string}>}
 */
export async function fetchPopularPalettes() {
  try {
    const db = getDatabase();
    const snap = await get(ref(db, 'publicPalettes/top'));
    const raw = snap.val();
    const palettes = Array.isArray(raw) ? raw : Object.values(raw || {});
    return { success: true, palettes };
  } catch (error) {
    console.error('Failed to fetch popular palettes:', error);
    return { success: false, error: error.message, palettes: [] };
  }
}

export async function fetchLastSyncedAt() {
  try {
    const db = getDatabase();
    const snap = await get(ref(db, 'publicPalettes/updatedAt'));
    return snap.val() || null;
  } catch {
    return null;
  }
}

/**
 * Fetch the 50 most recently published public palettes.
 * Pre-sorted by the ranking script — no client-side sorting needed.
 *
 * @returns {Promise<{success: boolean, palettes?: Array, error?: string}>}
 */
export async function fetchRecentPalettes() {
  try {
    const db = getDatabase();
    const snap = await get(ref(db, 'publicPalettes/recent'));
    const raw = snap.val();
    const palettes = Array.isArray(raw) ? raw : Object.values(raw || {});
    return { success: true, palettes };
  } catch (error) {
    console.error('Failed to fetch recent palettes:', error);
    return { success: false, error: error.message, palettes: [] };
  }
}

/**
 * Check if the current user has hearted a specific palette.
 *
 * @param {string} userId
 * @param {string} opaqueId - The palette's opaque gallery ID (palette.id from snapshot)
 * @returns {Promise<boolean>}
 */
export async function checkIfHearted(userId, opaqueId) {
  if (!userId || !opaqueId) return false;
  const db = getDatabase();
  const snap = await get(ref(db, `paletteHearts/${userId}/${opaqueId}`));
  return snap.exists();
}

/**
 * Toggle a heart on a public palette.
 * Returns the new hearted state after the toggle.
 *
 * @param {string} userId
 * @param {string} opaqueId - The palette's opaque gallery ID (palette.id from snapshot)
 * @returns {Promise<{success: boolean, hearted?: boolean, error?: string}>}
 */
export async function heartPalette(userId, opaqueId) {
  if (!userId || !opaqueId) {
    return { success: false, error: 'Invalid parameters' };
  }

  try {
    const db = getDatabase();
    const heartRef = ref(db, `paletteHearts/${userId}/${opaqueId}`);
    const alreadyHearted = (await get(heartRef)).exists();

    if (alreadyHearted) {
      await remove(heartRef);
      return { success: true, hearted: false };
    } else {
      await set(heartRef, true);
      return { success: true, hearted: true };
    }
  } catch (error) {
    console.error('Failed to toggle heart:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if a user has accepted the gallery Terms of Service.
 *
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export async function hasAcceptedGalleryToS(userId) {
  if (!userId) return false;
  const db = getDatabase();
  const snap = await get(ref(db, `users/${userId}/galleryToSAccepted`));
  return snap.exists() && snap.val() === true;
}

/**
 * Record that a user has accepted the gallery Terms of Service,
 * and optionally set their anonymous publish preference.
 *
 * @param {string} userId
 * @param {boolean} publishAnonymously
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function acceptGalleryToS(userId, publishAnonymously = false) {
  if (!userId) return { success: false, error: 'Invalid parameters' };
  try {
    const db = getDatabase();
    await update(ref(db, `users/${userId}`), {
      galleryToSAccepted: true,
      publishAnonymously: publishAnonymously
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to accept gallery ToS:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update a user's anonymous publish preference.
 *
 * @param {string} userId
 * @param {boolean} publishAnonymously
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function setPublishAnonymously(userId, publishAnonymously) {
  if (!userId) return { success: false, error: 'Invalid parameters' };
  try {
    const db = getDatabase();
    await update(ref(db, `users/${userId}`), { publishAnonymously });
    return { success: true };
  } catch (error) {
    console.error('Failed to update anonymous preference:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Toggle the hideLikes preference for a published palette.
 *
 * @param {string} userId
 * @param {string} paletteId
 * @param {boolean} hideLikes
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function updatePaletteHideLikes(userId, paletteId, hideLikes) {
  if (!userId || !paletteId) return { success: false, error: 'Invalid parameters' };
  try {
    const db = getDatabase();
    const fieldRef = ref(db, `palettes/${userId}/${paletteId}/hideLikes`);
    if (hideLikes) {
      await set(fieldRef, true);
    } else {
      await remove(fieldRef);
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Get a user's anonymous publish preference.
 *
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export async function getPublishAnonymously(userId) {
  if (!userId) return false;
  const db = getDatabase();
  const snap = await get(ref(db, `users/${userId}/publishAnonymously`));
  return snap.exists() && snap.val() === true;
}
